import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Appointments from "../../../appointments";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  Badge,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import KingBedIcon from "@mui/icons-material/KingBed";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import TransferWithinAStationIcon from "@mui/icons-material/TransferWithinAStation";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import EventNoteIcon from "@mui/icons-material/EventNote";
import BiotechIcon from "@mui/icons-material/Biotech";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";

import { IPD_SIDEBAR } from "../../utils/ipdSidebar";
import { useIpdData } from "../../context/IpdDataContext";
import PatientDetailsPanel, { ORDER_MGMT_OPEN_KEY } from "./PatientDetailsPanel";
import ReferredToMe from "./ReferredToMe";
import RejectedPatientsForReferral from "./RejectedPatientsForReferral";
import OrderSetsAdminPanel from "./OrderSetsAdminPanel";

// ─── Styled Components ────────────────────────────────────────────────────────

const RootWrapper = styled(Box)({
  display: "flex",
  height: "100vh",
  overflow: "hidden",
  backgroundColor: "#f0f4f8",
});

const SIDEBAR_WIDTH = 215;
const SIDEBAR_OPEN_KEY = "ipdDoctorSidebarOpen";
// Which patient is open, kept across a page reload. Nothing about the selection lives
// in the URL, so a refresh — or the browser restoring the tab — used to drop the doctor
// back on the bare patient list, closing whatever they had open on that patient.
const SELECTED_PATIENT_KEY = "ipdDoctorSelectedPatientId";

// Page size is measured, not fixed: show exactly the rows that fit on screen and put
// the rest on the next page. These are the parts of the table whose height never
// changes; whatever is left over after them is rows.
const TABLE_ROW_H = 49;
const TABLE_HEAD_H = 41;
const TABLE_PAGER_H = 52;
const TABLE_AREA_PADDING_BOTTOM = 20;
// A very short viewport still shows a usable list rather than one row and a pager.
const MIN_ROWS_PER_PAGE = 4;

// Collapsed means width 0, not `display: none` — the nav items stay mounted so the
// width can animate, and the fixed 215px inner width keeps labels from re-wrapping
// into a squashed mess on the way in and out.
const Sidebar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sidebaropen",
})(({ sidebaropen }) => ({
  width: sidebaropen ? SIDEBAR_WIDTH : 0,
  minWidth: sidebaropen ? SIDEBAR_WIDTH : 0,
  // Shared module-shell chrome (IPD_SIDEBAR), the same palette Inventory, Emergency,
  // IPD Billing, OT, Lab and Radiology sidebars use — this console used to be the one
  // left panel on its own colour (#121959 with a #21cdc0 active state).
  backgroundColor: IPD_SIDEBAR.bg,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  overflowY: sidebaropen ? "auto" : "hidden",
  overflowX: "hidden",
  transition: "width 0.2s ease, min-width 0.2s ease",
  // Children hold their full width while the parent animates to 0, so the labels
  // slide out instead of re-wrapping into a squashed column on the way. boxSizing is
  // explicit because this app ships no global border-box reset.
  "& > *": { width: SIDEBAR_WIDTH, flexShrink: 0, boxSizing: "border-box" },
}));

const SidebarBrand = styled(Box)({
  padding: "18px 16px 14px",
  borderBottom: `1px solid ${IPD_SIDEBAR.border}`,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 8,
});

// Only rendered while the sidebar is collapsed. Floats over MainArea rather than
// living in TopHeader, because TopHeader itself is hidden on the outpatient and
// orderSets sections — a toggle that lives there would strand the doctor with no
// way back to the nav.
const SidebarRestoreButton = styled(IconButton)({
  position: "absolute",
  top: 10,
  left: 10,
  zIndex: 10,
  padding: 6,
  // Icon sits on a white pill over the light content area — tinted to the sidebar it reopens.
  color: IPD_SIDEBAR.bg,
  backgroundColor: "rgba(255,255,255,0.92)",
  boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
  "&:hover": { backgroundColor: "#ffffff" },
});

const NavSectionLabel = styled(Typography)({
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: IPD_SIDEBAR.label,
  padding: "14px 16px 4px",
});

const NavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  display: "flex",
  alignItems: "center",
  gap: 11,
  padding: "10px 14px",
  cursor: "pointer",
  borderRadius: "0 24px 24px 0",
  marginRight: 10,
  transition: "all 0.18s",
  backgroundColor: active ? IPD_SIDEBAR.active : "transparent",
  color: active ? "#ffffff" : IPD_SIDEBAR.text,
  "& svg": { fontSize: "1.3rem", flexShrink: 0 },
  "&:hover": {
    backgroundColor: active ? IPD_SIDEBAR.active : IPD_SIDEBAR.hover,
    color: "#ffffff",
  },
}));

const NavItemLabel = styled(Typography)({
  fontSize: "0.8rem",
  fontWeight: 600,
  lineHeight: 1.25,
  whiteSpace: "nowrap",
});

const NavItemSub = styled(Typography)({
  fontSize: "0.65rem",
  lineHeight: 1.2,
  opacity: 0.8,
  whiteSpace: "nowrap",
});

const MainArea = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minWidth: 0,
  // Anchor for SidebarRestoreButton.
  position: "relative",
});

const TopHeader = styled(Box)({
  backgroundColor: "#ffffff",
  padding: "11px 20px",
  borderBottom: "1px solid #e4e9ef",
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexShrink: 0,
});

const StatsRow = styled(Box)({
  display: "flex",
  gap: 12,
  padding: "4px 20px 12px",
  flexShrink: 0,
});

const StatCard = styled(Paper)(({ cardbg }) => ({
  flex: 1,
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  borderRadius: 12,
  backgroundColor: cardbg || "#ffffff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  border: "1px solid rgba(0,0,0,0.04)",
}));

const StatIconWrap = styled(Box)({
  flexShrink: 0,
  "& svg": { fontSize: "2rem" },
});

const TableArea = styled(Box)({
  flex: 1,
  overflow: "auto",
  padding: "0 20px 20px",
});

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "isselected",
})(({ isselected }) => ({
  cursor: "pointer",
  transition: "background 0.15s",
  backgroundColor: isselected === "true" ? "#EBFAF9" : "transparent",
  "&:hover": {
    backgroundColor: isselected === "true" ? "#d5f5f2" : "#f5fffe",
  },
}));

// ── Right panel sizing ──
// The panel is drag-resizable, so its width is state, not a constant. These bound it:
// narrower than MIN and PatientDetailsPanel's two-column vitals/action rows start
// wrapping badly; wider than MAX (or leaving MainArea under its minimum) and the patient
// list it sits next to stops being readable.
const RIGHT_PANEL_DEFAULT_WIDTH = 500;
const RIGHT_PANEL_MIN_WIDTH = 360;
const RIGHT_PANEL_MAX_WIDTH = 900;
const MAIN_AREA_MIN_WIDTH = 520;
const RIGHT_PANEL_WIDTH_KEY = "ipdDoctorRightPanelWidth";

// MAX is also capped by what's actually on screen — a 900px panel on a 1280px laptop
// would leave the table unusable. Math.max at the end keeps MIN winning on narrow
// screens, where the viewport cap would otherwise drop below it.
const clampRightPanelWidth = (px) => {
  const max = Math.max(
    RIGHT_PANEL_MIN_WIDTH,
    Math.min(RIGHT_PANEL_MAX_WIDTH, window.innerWidth - MAIN_AREA_MIN_WIDTH),
  );
  return Math.min(Math.max(px, RIGHT_PANEL_MIN_WIDTH), max);
};

// 6px strip between MainArea and RightPanel. Transparent until hovered so it doesn't
// read as a second border next to the panel's own.
const ResizeHandle = styled(Box)({
  width: 6,
  flexShrink: 0,
  cursor: "col-resize",
  backgroundColor: "transparent",
  transition: "background-color 0.15s",
  "&:hover": { backgroundColor: "#21cdc0" },
  "&:active": { backgroundColor: "#21cdc0" },
});

const RightPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "panelwidth",
})(({ theme, panelwidth }) => ({
  width: panelwidth,
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fafafa",
  padding: 8,
  flexShrink: 0,
  overflowY: "auto",
}));

const EmptyState = styled(Paper)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  backgroundColor: "transparent",
  boxShadow: "none",
}));

// ─── Component ────────────────────────────────────────────────────────────────

const DoctorConsoleIpd = () => {
  const { getPatients, searchPatients: searchPatientsApi, addCarePlan, stopMedication } = useIpdData();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(() => {
    try {
      return sessionStorage.getItem(SELECTED_PATIENT_KEY) || null;
    } catch {
      return null;
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  // [NEW CODE - Referred to Me feature]
  const [consultationPatients, setConsultationPatients] = useState([]);
  const [department, setDepartment] = useState("All");
  const history = useHistory();
  const [activeSection, setActiveSection] = useState("admitted");

  // ── Notification badge counts ──────────────────────────────────────────────
  const [referralCount, setReferralCount] = useState(0);
  const [rejectionCount, setRejectionCount] = useState(0);
  // seenCounts persisted to localStorage so badge doesn't reappear after page refresh
  const [seenCounts, setSeenCounts] = useState(() => {
    try {
      const stored = localStorage.getItem("ipdDoctorSeenCounts");
      return stored ? JSON.parse(stored) : { admitted: 0, referral: 0, rejected: 0 };
    } catch {
      return { admitted: 0, referral: 0, rejected: 0 };
    }
  });

  // Persist seenCounts so badge stays cleared after page refresh
  useEffect(() => {
    try { localStorage.setItem("ipdDoctorSeenCounts", JSON.stringify(seenCounts)); } catch {}
  }, [seenCounts]);

  // ── Reload straight back into Order Management ────────────────────────────
  // The selection is restored from sessionStorage immediately, but `patients` is empty
  // until ipd_admission/ answers — so selectedPatient is undefined for that window, the
  // details panel (and with it the full-screen Order Management) does not render, and the
  // workbench shows through. The reload then looks like it bounced out to the patient
  // list and back. Hold a neutral screen for exactly that window instead.
  //
  // Read once at mount: PatientDetailsPanel clears the key as soon as it mounts with the
  // drawer closed, so re-reading it later would flip this mid-restore.
  const [restoringOrderMgmt, setRestoringOrderMgmt] = useState(() => {
    try {
      const openFor = sessionStorage.getItem(ORDER_MGMT_OPEN_KEY);
      return Boolean(openFor) && openFor === sessionStorage.getItem(SELECTED_PATIENT_KEY);
    } catch {
      return false;
    }
  });

  // Only the *first* load is a restore. Without this the placeholder would come back
  // every time the Refresh button set loading — covering the console for no reason.
  useEffect(() => {
    if (!loading) setRestoringOrderMgmt(false);
  }, [loading]);

  // See SELECTED_PATIENT_KEY. sessionStorage rather than localStorage: this is "where I
  // am right now", which should not follow the doctor into a brand new session.
  useEffect(() => {
    try {
      if (selectedPatientId) sessionStorage.setItem(SELECTED_PATIENT_KEY, String(selectedPatientId));
      else sessionStorage.removeItem(SELECTED_PATIENT_KEY);
    } catch {}
  }, [selectedPatientId]);

  // ── Collapsible sidebar ───────────────────────────────────────────────────
  // Persisted like seenCounts above — a doctor who works with the nav collapsed
  // shouldn't have to collapse it again on every visit.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_OPEN_KEY) !== "false";
    } catch {
      return true;
    }
  });

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      try { localStorage.setItem(SIDEBAR_OPEN_KEY, String(!prev)); } catch {}
      return !prev;
    });
  };

  // ── Resizable right panel ─────────────────────────────────────────────────
  // Width persists per browser (same pattern as seenCounts above) — a doctor who
  // widens the panel for a long care plan shouldn't have to redo it every login.
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    try {
      const stored = Number(localStorage.getItem(RIGHT_PANEL_WIDTH_KEY));
      return stored ? clampRightPanelWidth(stored) : RIGHT_PANEL_DEFAULT_WIDTH;
    } catch {
      return RIGHT_PANEL_DEFAULT_WIDTH;
    }
  });

  // A stored width valid on a 1920 monitor can be far too wide once the same browser
  // opens on a laptop, so re-clamp whenever the viewport changes.
  useEffect(() => {
    const onWindowResize = () => setRightPanelWidth((w) => clampRightPanelWidth(w));
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  // The drag listens on window, not on the handle: a pointer moving faster than React
  // re-renders leaves a 6px strip immediately, and mouseup can land anywhere on the page.
  const startPanelResize = (e) => {
    e.preventDefault();
    // Panel is flush to the right edge, so its width is simply the distance from the
    // cursor to that edge.
    const widthFromCursor = (ev) => clampRightPanelWidth(window.innerWidth - ev.clientX);

    const onMouseMove = (ev) => setRightPanelWidth(widthFromCursor(ev));
    const onMouseUp = (ev) => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      try { localStorage.setItem(RIGHT_PANEL_WIDTH_KEY, String(widthFromCursor(ev))); } catch {}
    };

    // Without these the drag selects text across the patient table, and the cursor
    // flickers back to a caret every time it leaves the handle mid-drag.
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const resetPanelWidth = () => {
    setRightPanelWidth(RIGHT_PANEL_DEFAULT_WIDTH);
    try { localStorage.setItem(RIGHT_PANEL_WIDTH_KEY, String(RIGHT_PANEL_DEFAULT_WIDTH)); } catch {}
  };

  // Navigate to a section and immediately mark its current count as seen.
  // Also updated by onCountChange (when the component reports its actual fetch result).
  const goToSection = (section) => {
    setActiveSection(section);
    setSeenCounts((prev) => ({
      ...prev,
      [section]:
        section === "admitted" ? patients.length :
        section === "referral" ? referralCount :
        section === "rejected" ? rejectionCount :
        prev[section],
    }));
  };

  // If count changes while user is ACTIVELY viewing that section → keep seenCounts in sync
  // so the badge stays at 0 regardless of when data arrives.
  useEffect(() => {
    if (activeSection === "rejected") {
      setSeenCounts((prev) => ({ ...prev, rejected: rejectionCount }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejectionCount]);

  useEffect(() => {
    if (activeSection === "referral") {
      setSeenCounts((prev) => ({ ...prev, referral: referralCount }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCount]);

  // Badge = new items since the doctor last visited that section
  const navBadge = (section) => {
    if (activeSection === section) return 0;
    const current =
      section === "admitted" ? patients.length :
      section === "referral" ? referralCount :
      section === "rejected" ? rejectionCount : 0;
    return Math.max(0, current - (seenCounts[section] ?? 0));
  };

  // Re-fetches the admitted list in place. The Refresh button above the table used to
  // call window.location.reload(), which threw away everything the doctor had open —
  // the selected patient, the Order Management screen, scroll position — to re-read one
  // endpoint. This only re-reads the endpoint.
  const loadPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPatients();
      if (response.status === 200) {
        setPatients(response.data);
        setFilteredPatients(response.data);
        // Mark admitted as seen immediately (doctor starts here)
        setSeenCounts((prev) => ({ ...prev, admitted: response.data.length }));
      }
    } catch (error) {
      console.error("Failed to fetch patients:", error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch patients on mount
  useEffect(() => {
    loadPatients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background fetch: get referral + rejection counts so badges show before visiting those tabs
  useEffect(() => {
    const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL || "http://localhost.dev.com:8000/app/api/";
    const headers = {
      Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
      "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
      "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
      "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
      USERNAME: sessionStorage.getItem("userName") || "",
    };
    const getLen = (raw) => {
      if (Array.isArray(raw?.data)) return raw.data.length;
      if (Array.isArray(raw?.results)) return raw.results.length;
      if (Array.isArray(raw)) return raw.length;
      return 0;
    };
    axios.get(`${BASE}ipd_admission/referred_to_me/?status=Allocated&since=2026-01-01`, { headers })
      .then((r) => setReferralCount(getLen(r.data))).catch(() => {});
    axios.get(`${BASE}ipd_admission/my_rejections/`, { headers })
      .then((r) => setRejectionCount(getLen(r.data))).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // These options are bed_type_name values ("Deluxe", "ICU"), NOT wards — the
  // `ward*` names here are historical. The UI is labelled "Bed Type" to match
  // what this actually filters on; the real ward is a separate field (ward_name).
  const wardOptions = useMemo(() => {
    const wards = patients
      .map((p) => p.bed_type_name)
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort();
    return wards;
  }, [patients]);

  // Apply ward filter on top of base patient list
  const applyWardFilter = (list) =>
    department === "All" ? list : list.filter((p) => p.bed_type_name === department);

  // ── Newest admission first ────────────────────────────────────────────────
  // ipd_admission/ returns oldest-first, so a doctor had to scroll to the bottom to
  // find the patient just admitted.
  //
  // Ordered by admission id, not admission date. The id is what the backend increments
  // per admission, so it orders them exactly; admissionDate is only ever a date —
  // IpdDataContext strips the time off admitted_at — so it cannot separate the
  // same-day admissions that make up most of a live ward, and its format is whatever
  // the API sends (Date.parse returns NaN on anything non-ISO). Date is kept purely as
  // a fallback for a tenant whose ids are not numeric.
  const sortedPatients = useMemo(() => {
    const idNum = (p) => {
      const n = Number(p?.admissionId ?? p?.id);
      return Number.isFinite(n) ? n : 0;
    };
    const dayMs = (p) => {
      const t = Date.parse(p?.admissionDate || "");
      return Number.isFinite(t) ? t : 0;
    };
    return [...filteredPatients].sort((a, b) => idNum(b) - idNum(a) || dayMs(b) - dayMs(a));
  }, [filteredPatients]);

  // ── Pagination ────────────────────────────────────────────────────────────
  // Client-side: ipd_admission/ takes no page/limit params and returns the doctor's
  // whole list in one call, so this splits what is already in memory. If the endpoint
  // ever grows paging params, move page/rowsPerPage into that request instead.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const listAreaRef = useRef(null);
  const listHeaderRef = useRef(null);

  // ── Shell height ──────────────────────────────────────────────────────────
  // RootWrapper asks for 100vh, but this console renders *under* DoctorLayout's navbar
  // and LayoutWithbg's 96px banner — so a full viewport made the shell taller than the
  // screen, and its bottom (the last rows and the pager) sat below the fold. Measuring
  // the shell's own offset instead of hardcoding the chrome height keeps this right if
  // what renders above ever changes.
  //
  // Row fitting below depends on this: without it the list is fitted to a container
  // that itself runs off-screen, which is how "as many as fit" still needed scrolling.
  const rootRef = useRef(null);
  const [shellHeight, setShellHeight] = useState("100vh");

  useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      // Document-relative, so it does not change with the page's scroll position.
      const offsetFromDocumentTop = el.getBoundingClientRect().top + window.scrollY;
      setShellHeight(`calc(100vh - ${Math.max(0, Math.round(offsetFromDocumentTop))}px)`);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Re-measured on every size change — the sidebar collapsing, the right panel being
  // dragged and the window resizing all change how much height the list gets, and the
  // first two move the container without the window resizing at all (so ResizeObserver,
  // not a window listener).
  useEffect(() => {
    const area = listAreaRef.current;
    if (!area) return;
    const recompute = () => {
      const headerH = listHeaderRef.current?.offsetHeight || 0;
      const available =
        area.clientHeight - headerH - TABLE_HEAD_H - TABLE_PAGER_H - TABLE_AREA_PADDING_BOTTOM;
      setRowsPerPage(Math.max(MIN_ROWS_PER_PAGE, Math.floor(available / TABLE_ROW_H)));
    };
    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(area);
    return () => observer.disconnect();
  }, [activeSection, loading]);

  // A search or bed-type change rebuilds the list under the current page — start over
  // at the first page rather than landing on one the new result set doesn't have.
  useEffect(() => {
    setPage(0);
  }, [searchQuery, department, activeSection]);

  // Clamped at render rather than in an effect, so a list that shrank for any other
  // reason (refresh, discharge) can never leave the table showing an empty page.
  const pageCount = Math.max(1, Math.ceil(sortedPatients.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pagedPatients = sortedPatients.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage,
  );

  // Handle search + ward filter
  useEffect(() => {
    const runSearch = async () => {
      if (!searchQuery.trim()) {
        setFilteredPatients(applyWardFilter(patients));
        return;
      }
      const response = await searchPatientsApi(searchQuery);
      if (response.status === 200) {
        setFilteredPatients(applyWardFilter(response.data));
      }
    };
    const debounceTimer = setTimeout(runSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, patients, department]);

  // [NEW CODE - Referred to Me feature]
  const selectedPatient =
    patients.find((p) => p.id === selectedPatientId) ||
    consultationPatients.find(
      (p) =>
        p.id === selectedPatientId &&
        (p.type !== "Referral" || p.referralAccepted)
    );

  // [NEW CODE - Referred to Me feature]
  const handleSelectPatient = (patient) => {
    if (!patient) {
      setSelectedPatientId(null);
      return;
    }
    if (patient.type === "Referral" && !patient.referralAccepted) {
      return;
    }
    const existsInMain = patients.some((p) => String(p.id) === String(patient.id));
    const existsInConsultation = consultationPatients.some(
      (p) => String(p.id) === String(patient.id)
    );
    if (!existsInMain && !existsInConsultation) {
      setConsultationPatients((prev) => [...prev, patient]);
    } else if (patient.referralAccepted) {
      setConsultationPatients((prev) =>
        prev.map((p) =>
          String(p.id) === String(patient.id)
            ? { ...p, ...patient, referralAccepted: true }
            : p
        )
      );
    }
    setSelectedPatientId(patient.id);
  };

  const handleAddCarePlan = async (patientId, carePlan) => {
    const response = await addCarePlan(patientId, carePlan);
    if (response.status === 201) {
      const added = response.data;
      const structureId = Number(added?.data?.structureId ?? added?.data?.structure_id ?? 0);
      const isManualActivitySave =
        added?.data?.values != null && typeof added.data.values === "object";
      setPatients((prev) =>
        prev.map((patient) => {
          if (patient.id !== patientId) return patient;
          let plans = Array.isArray(patient.carePlans) ? [...patient.carePlans] : [];
          if (structureId > 0 && isManualActivitySave) {
            plans = plans.filter(
              (cp) => Number(cp?.data?.structureId ?? cp?.data?.structure_id ?? 0) !== structureId
            );
          }
          return { ...patient, carePlans: [...plans, added] };
        })
      );
      // [NEW CODE - Referred to Me feature]
      setConsultationPatients((prev) =>
        prev.map((patient) => {
          if (patient.id !== patientId) return patient;
          let plans = Array.isArray(patient.carePlans) ? [...patient.carePlans] : [];
          if (structureId > 0 && isManualActivitySave) {
            plans = plans.filter(
              (cp) => Number(cp?.data?.structureId ?? cp?.data?.structure_id ?? 0) !== structureId
            );
          }
          return { ...patient, carePlans: [...plans, added] };
        })
      );
    }
  };

  const handleStopMedication = async (patientId, carePlanId, medicationId) => {
    const response = await stopMedication(patientId, carePlanId, medicationId);
    if (response.status === 200) {
      setPatients((prev) =>
        prev.map((patient) => {
          if (patient.id !== patientId) return patient;
          return {
            ...patient,
            carePlans: patient.carePlans.map((cp) =>
              cp.id === carePlanId ? response.data : cp
            ),
          };
        })
      );
      // [NEW CODE - Referred to Me feature]
      setConsultationPatients((prev) =>
        prev.map((patient) => {
          if (patient.id !== patientId) return patient;
          return {
            ...patient,
            carePlans: patient.carePlans.map((cp) =>
              cp.id === carePlanId ? response.data : cp
            ),
          };
        })
      );
    }
  };

  // ─── Derived data ──────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    const newAdmissions = patients.filter(
      (p) => p.admissionDate && String(p.admissionDate).startsWith(today)
    ).length;
    const icu = patients.filter((p) =>
      p.bed_type_name?.toLowerCase().includes("icu")
    ).length;
    const discharged = patients.filter((p) =>
      p.dischargeInitiated || p.status?.toLowerCase?.() === "discharged"
    ).length;
    const testsReady = patients.filter(
      (p) => Array.isArray(p.carePlans) && p.carePlans.some((cp) => cp?.type === "Test")
    ).length;
    return [
      {
        label: "Total IPD Patients",
        value: patients.length,
        icon: <PeopleIcon sx={{ color: "#3B82F6" }} />,
      },
      {
        label: "New Admissions",
        value: newAdmissions,
        icon: <PersonAddAltIcon sx={{ color: "#22C55E" }} />,
      },
      {
        label: "ICU / Critical",
        value: icu,
        icon: <LocalHospitalIcon sx={{ color: "#F59E0B" }} />,
      },
      {
        label: "Discharged",
        value: discharged,
        icon: <AssignmentTurnedInIcon sx={{ color: "#7C3AED" }} />,
      },
      {
        label: "Tests Ready",
        value: testsReady,
        icon: <BiotechIcon sx={{ color: "#1E40AF" }} />,
      },
    ];
  }, [patients]);

  const today = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  // Stands in for Order Management until the patient it belongs to is back. White on
  // white with the drawer it is about to become, so the reload reads as one screen
  // loading rather than two screens swapping. Placed after every hook above, so the
  // hook order is identical on both branches.
  if (restoringOrderMgmt && loading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          zIndex: 1400,
        }}
      >
        <CircularProgress sx={{ color: "#21cdc0" }} />
        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
          Loading Order Management…
        </Typography>
      </Box>
    );
  }

  return (
    <RootWrapper ref={rootRef} style={{ height: shellHeight }}>
      {/* ── Sidebar ── */}
      <Sidebar sidebaropen={sidebarOpen}>
        <SidebarBrand>
          <Box>
            {/* White title over a muted label, the same brand treatment the other module
                shells use (Lab/OT/Radiology "HTMATE" + subtitle). */}
            <Typography sx={{ color: "#ffffff", fontWeight: 800, fontSize: "0.92rem", lineHeight: 1.2 }}>
              Doctor's
            </Typography>
            <Typography sx={{ color: IPD_SIDEBAR.label, fontSize: "0.72rem" }}>
              IPD Workbench
            </Typography>
          </Box>
          <Tooltip title="Collapse menu" placement="right">
            <IconButton
              size="small"
              onClick={toggleSidebar}
              sx={{ color: IPD_SIDEBAR.label, p: 0.5, "&:hover": { color: "#ffffff", backgroundColor: IPD_SIDEBAR.hover } }}
            >
              <ViewSidebarOutlinedIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>
          </Tooltip>
        </SidebarBrand>

        <NavSectionLabel>Menu</NavSectionLabel>

        <NavItem active={activeSection === "outpatient"} onClick={() => goToSection("outpatient")}>
          <PersonAddAltIcon />
          <Box>
            <NavItemLabel>Outpatient</NavItemLabel>
            <NavItemSub>Today's OPD Patients</NavItemSub>
          </Box>
        </NavItem>

        <NavItem active={activeSection === "admitted"} onClick={() => goToSection("admitted")}>
          <Badge
            badgeContent={navBadge("admitted")}
            max={99}
            sx={{ "& .MuiBadge-badge": { bgcolor: "#f87171", color: "#fff", fontSize: "0.6rem", fontWeight: 700, minWidth: 16, height: 16, borderRadius: "8px" } }}
          >
            <KingBedIcon />
          </Badge>
          <Box>
            <NavItemLabel>Admitted Patients</NavItemLabel>
            <NavItemSub>Inpatients / IPD</NavItemSub>
          </Box>
        </NavItem>

        <NavItem active={activeSection === "referral"} onClick={() => goToSection("referral")}>
          <Badge
            badgeContent={navBadge("referral")}
            max={99}
            sx={{ "& .MuiBadge-badge": { bgcolor: "#f87171", color: "#fff", fontSize: "0.6rem", fontWeight: 700, minWidth: 16, height: 16, borderRadius: "8px" } }}
          >
            <TransferWithinAStationIcon />
          </Badge>
          <Box>
            <NavItemLabel>Referral Patients</NavItemLabel>
            <NavItemSub>Referred In / Out</NavItemSub>
          </Box>
        </NavItem>

        <NavItem active={activeSection === "rejected"} onClick={() => goToSection("rejected")}>
          <Badge
            badgeContent={navBadge("rejected")}
            max={99}
            sx={{ "& .MuiBadge-badge": { bgcolor: "#f87171", color: "#fff", fontSize: "0.6rem", fontWeight: 700, minWidth: 16, height: 16, borderRadius: "8px" } }}
          >
            <PersonRemoveIcon />
          </Badge>
          <Box>
            <NavItemLabel>Rejected Patients</NavItemLabel>
            <NavItemSub>Rejected Referrals</NavItemSub>
          </Box>
        </NavItem>

        <NavItem
          active={false}
          onClick={() => {
            sessionStorage.setItem("mainLandingActiveSection", "default");
            // Full navigation so we leave doctor_console_ipd layout for OPD main-landing
            window.location.assign("/main-landing");
          }}
        >
          <EventNoteIcon />
          <Box>
            <NavItemLabel>Appointments</NavItemLabel>
            <NavItemSub>Today's Appointments</NavItemSub>
          </Box>
        </NavItem>

        <NavItem active={activeSection === "orderSets"} onClick={() => goToSection("orderSets")}>
          <AssignmentIcon />
          <Box>
            <NavItemLabel>Order Set</NavItemLabel>
            <NavItemSub>Manage Prescription Sets</NavItemSub>
          </Box>
        </NavItem>

        <Box sx={{ flexGrow: 1 }} />
      </Sidebar>

      {/* ── Main Area ── */}
      <MainArea>
        {!sidebarOpen && (
          <Tooltip title="Show menu" placement="right">
            <SidebarRestoreButton size="small" onClick={toggleSidebar}>
              <ViewSidebarOutlinedIcon sx={{ fontSize: "1.1rem" }} />
            </SidebarRestoreButton>
          </Tooltip>
        )}

        {/* Top Header — hidden when outpatient/OPD or Order Set section is active */}
        {/* Extra left padding while collapsed so the floating restore button doesn't
            sit on top of the "Doctor's Workbench" title. */}
        {activeSection !== "outpatient" && activeSection !== "orderSets" && <TopHeader sx={sidebarOpen ? undefined : { pl: 6 }}>
          <Box sx={{ flexShrink: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#121959", lineHeight: 1.2 }}>
              Doctor's Workbench
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              {activeSection === "admitted" && "Inpatients / IPD"}
              {activeSection === "referral" && "Referral Patients"}
              {activeSection === "rejected" && "Rejected Referral Patients"}
              {activeSection === "outpatient" && "Today's OPD Patients"}
              {activeSection === "appointments" && "Today's Appointments"}
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Search patients…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, maxWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, ml: "auto" }}>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={{ border: "1px solid #dde3ea", borderRadius: "50%", color: "#555" }}
                onClick={loadPatients}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Select
              size="small"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              sx={{
                fontSize: "0.82rem", minWidth: 148,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dde3ea" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#21cdc0" },
                "& .MuiSelect-select": { py: "6px" },
              }}
            >
              <MenuItem value="All" sx={{ fontSize: "0.82rem" }}>All Bed Types</MenuItem>
              {wardOptions.map((ward) => (
                <MenuItem key={ward} value={ward} sx={{ fontSize: "0.82rem" }}>{ward}</MenuItem>
              ))}
            </Select>
          </Box>
        </TopHeader>}

        {/* ── Section Content ── */}
        <TableArea ref={listAreaRef}>

          {/* ADMITTED PATIENTS */}
          {activeSection === "admitted" && (
            <>
{loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8, mt: 2 }}>
                  <CircularProgress sx={{ color: "#21cdc0" }} />
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #e4e9ef", mt: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          { key: "#", align: "left" },
                          { key: "Patient Details", align: "left" },
                          { key: "UHID", align: "left" },
                          { key: "Bed", align: "left" },
                          { key: "Ward", align: "left" },
                          { key: "Location", align: "left" },
                          { key: "Diagnosis", align: "left" },
                        ].map(({ key, align }) => (
                          <TableCell key={key} align={align} sx={{
                            fontWeight: 700, fontSize: "0.75rem", color: "#121959",
                            backgroundColor: "#ffffff", borderBottom: "2px solid #e4e9ef",
                            whiteSpace: "nowrap", py: 1.3,
                          }}>
                            {key}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedPatients.map((patient, index) => {
                        const isSelected = String(patient.id) === String(selectedPatientId);
                        const hasLabResult = Array.isArray(patient.carePlans) &&
                          patient.carePlans.some((cp) => cp?.type === "Test");
                        const hasRadiology = Array.isArray(patient.carePlans) &&
                          patient.carePlans.some((cp) => cp?.type === "Radiology");
                        return (
                          <StyledTableRow
                            key={patient.id}
                            isselected={String(isSelected)}
                            onClick={() => setSelectedPatientId(patient.id)}
                          >
                            {/* Continues across pages — page 2 starts at 11, not 1. */}
                            <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary", fontWeight: 500, width: 36, pl: 2 }}>
                              {safePage * rowsPerPage + index + 1}
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Avatar sx={{
                                  width: 34, height: 34, fontSize: "0.7rem", fontWeight: 700,
                                  backgroundColor: isSelected ? "#21cdc0" : "#ddf4f2",
                                  color: isSelected ? "#fff" : "#121959", flexShrink: 0,
                                }}>
                                  {getInitials(patient.name)}
                                </Avatar>
                                <Box>
                                  <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "#121959" }}>
                                    {patient.name}
                                  </Typography>
                                  {(patient.age || patient.gender) && (
                                    <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                                      {[patient.age && `${patient.age} Y`, patient.gender].filter(Boolean).join(" / ")}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: "0.78rem", color: "#374151" }}>
                                {patient.uhid || patient.patient_id || patient.pat_id || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: "0.78rem", color: "#374151", fontWeight: 500 }}>
                                {patient.bedNo || patient.bed_name || patient.bed_id || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={patient.bed_type_name || "Admitted"} size="small" sx={{
                                fontSize: "0.68rem", height: 22, fontWeight: 600,
                                backgroundColor: "#e8faf9", color: "#0a7a73", border: "1px solid #b2ebe0",
                              }} />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 150 }}>
                              <Typography sx={{
                                fontSize: "0.78rem", color: "#374151",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}>
                                {patient.location || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 140 }}>
                              <Typography sx={{
                                fontSize: "0.78rem", color: "#374151",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}>
                                {patient.condition || "-"}
                              </Typography>
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                      {sortedPatients.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, color: "text.secondary", fontSize: "0.85rem" }}>
                            {searchQuery ? `No patients found matching "${searchQuery}"` : "No patients found"}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {sortedPatients.length > 0 && (
                    <TablePagination
                      component="div"
                      count={sortedPatients.length}
                      page={safePage}
                      onPageChange={(_, next) => setPage(next)}
                      rowsPerPage={rowsPerPage}
                      // No page-size picker: the size is whatever fits on screen, so
                      // letting it be chosen would put the scrolling straight back.
                      rowsPerPageOptions={[]}
                      sx={{
                        borderTop: "1px solid #e4e9ef",
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        },
                      }}
                    />
                  )}
                </TableContainer>
              )}
            </>
          )}

          {/* REFERRAL PATIENTS */}
          {activeSection === "referral" && (
            <ReferredToMe
              patients={patients}
              selectedPatientId={selectedPatientId}
              onSelectPatient={handleSelectPatient}
              onCountChange={(n) => {
                setReferralCount(n);
                // User is looking at this section right now → mark as seen immediately
                setSeenCounts((prev) => ({ ...prev, referral: n }));
              }}
            />
          )}

          {/* REJECTED REFERRAL PATIENTS */}
          {activeSection === "rejected" && (
            <RejectedPatientsForReferral
              onCountChange={(n) => {
                setRejectionCount(n);
                // User is looking at this section right now → mark as seen immediately
                setSeenCounts((prev) => ({ ...prev, rejected: n }));
              }}
            />
          )}

          {/* OPD Appointments rendered inline so the IPD sidebar stays visible */}
          {activeSection === "outpatient" && (
            <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
              <Appointments />
            </Box>
          )}

          {activeSection === "appointments" && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60%", gap: 1.5 }}>
              <KingBedIcon sx={{ fontSize: "3.5rem", color: "#d1d5db" }} />
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#9ca3af" }}>Appointments</Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "#d1d5db" }}>Coming soon</Typography>
            </Box>
          )}

          {/* Order Sets are hospital-wide master data, not tied to a patient — managed here, applied to a patient from Order Management */}
          {activeSection === "orderSets" && (
            <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
              <OrderSetsAdminPanel />
            </Box>
          )}

        </TableArea>
      </MainArea>

      {/* ── Right Panel — hidden on outpatient/orderSets sections ── */}
      {activeSection !== "outpatient" && activeSection !== "orderSets" && <>
        <ResizeHandle
          onMouseDown={startPanelResize}
          onDoubleClick={resetPanelWidth}
          title="Drag to resize · double-click to reset"
        />
        <RightPanel panelwidth={rightPanelWidth}>
        {selectedPatient ? (
          <PatientDetailsPanel
            patient={selectedPatient}
            onAddCarePlan={handleAddCarePlan}
            onStopMedication={handleStopMedication}
            onClose={() => setSelectedPatientId(null)}
          />
        ) : (
          <EmptyState>
            <PeopleIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Select A Patient
            </Typography>
            <Typography variant="body2" color="text.disabled" textAlign="center">
              Click on a patient card to view their details and manage care plans
            </Typography>
          </EmptyState>
        )}
        </RightPanel>
      </>}
    </RootWrapper>
  );
};

export default DoctorConsoleIpd;
